import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Skeleton,
  IconButton,
  Stack,
  Button,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import Avatar from '@mui/material/Avatar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { useParams } from 'react-router-dom';
import config from '../../Config';
import AddMappingView from './AddUpsaleMapping';
import EditIcon from '@mui/icons-material/Edit';
import Swal from 'sweetalert2';

export default function UpsaleCategories() {
  const { propertyId } = useParams();

  const [mappedHierarchy, setMappedHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedTableCategory, setSelectedTableCategory] = useState(null);
  const [selectedTableSubCategory, setSelectedTableSubCategory] = useState(null);

  const [showAddMapping, setShowAddMapping] = useState(false);
  const [deleting, setDeleting] = useState({});

  useEffect(() => {
    fetchMappedHierarchy();
  }, []);

  const fetchMappedHierarchy = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.apiBaseUrl}/admin/upsale/property/${propertyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (data.success) {
        const mapped = (data.data.categories || [])
          .filter((catItem) => catItem.category)
          .map((catItem) => ({
            id: catItem.category._id,
            name: catItem.category.name,
            hasSubCategory: catItem.category.hasSubCategory ?? false,

            products: (catItem.products || [])
              .filter((p) => p.product)
              .map((p) => ({
                id: p.product._id,
                name: p.product.name,
                image: p.product.image,
                price: p.price,
              })),

            subCategories: (catItem.subCategories || [])
              .filter((s) => s.subCategory)
              .map((s) => ({
                id: s.subCategory._id,
                name: s.subCategory.name,

                products: (s.products || [])
                  .filter((p) => p.product)
                  .map((p) => ({
                    id: p.product._id,
                    name: p.product.name,
                    image: p.product.image,
                    price: p.price,
                  })),
              })),
          }));

        setMappedHierarchy(mapped);
      } else {
        setMappedHierarchy([]);
      }
    } catch {
      setMappedHierarchy([]);
    } finally {
      setLoading(false);
    }
  };

  const removeProduct = async (productId) => {
    setDeleting((p) => ({ ...p, [productId]: true }));
    try {
      const token = localStorage.getItem('token');
      await fetch(
        `${config.apiBaseUrl}/admin/upsale/property/${propertyId}/remove-product/${productId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchMappedHierarchy();
    } finally {
      setDeleting((p) => ({ ...p, [productId]: false }));
    }
  };

  const removeSubCategory = async (subCategoryId) => {
    setDeleting((p) => ({ ...p, [subCategoryId]: true }));
    try {
      const token = localStorage.getItem('token');
      await fetch(
        `${config.apiBaseUrl}/admin/upsale/property/${propertyId}/remove-subcategory/${subCategoryId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchMappedHierarchy();
    } finally {
      setDeleting((p) => ({ ...p, [subCategoryId]: false }));
    }
  };

  const removeCategory = async (categoryId) => {
    setDeleting((p) => ({ ...p, [categoryId]: true }));
    try {
      const token = localStorage.getItem('token');
      await fetch(
        `${config.apiBaseUrl}/admin/upsale/property/${propertyId}/remove-category/${categoryId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchMappedHierarchy();
    } finally {
      setDeleting((p) => ({ ...p, [categoryId]: false }));
    }
  };

  const handleEditProduct = async (product) => {
    const { value: formValues } = await Swal.fire({
      title: 'Edit Product Pricing',
      html: `
        <div style="
          display:flex;
          flex-direction:column;
          gap:16px;
          text-align:left;
          margin-top:12px;
        ">

          <div>
            <label style="
              font-size:13px;
              font-weight:600;
              color:#374151;
              display:block;
              margin-bottom:6px;
            ">
              Price Type
            </label>

            <select
              id="priceType"
              class="swal2-input"
              style="
                width:100%;
                margin:0;
              "
            >
              <option value="">Select pricing model</option>
              <option value="hourly">Per Hour</option>
              <option value="fixed">Fixed</option>
            </select>

            <small style="color:#6b7280">
              Choose how this product will be charged
            </small>
          </div>

          <div>
            <label style="
              font-size:13px;
              font-weight:600;
              color:#374151;
              display:block;
              margin-bottom:6px;
            ">
              Price
            </label>

            <div style="display:flex; align-items:stretch;">
              <!-- Currency -->
              <span style="
                display:flex;
                align-items:center;
                justify-content:center;
                width:42px;
                height:42px;
                background:#f9fafb;
                border:1px solid #d1d5db;
                border-right:none;
                border-radius:6px 0 0 6px;
                font-weight:600;
                color:#374151;
                font-size:14px;
                box-sizing:border-box;
              ">
                £
              </span>

              <!-- Input -->
              <input
                id="price"
                type="number"
                min="0"
                class="swal2-input"
                placeholder="0.00"
                style="
                  height:42px;
                  margin:0;
                  padding:0 12px;
                  border-radius:0 6px 6px 0;
                  box-sizing:border-box;
                "
              />
            </div>

            <small style="color:#6b7280">
              Enter the final selling price
            </small>
          </div>


        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Save Changes',
      confirmButtonColor: '#2563eb',
      cancelButtonText: 'Cancel',
      cancelButtonColor: '#9ca3af',
      focusConfirm: false,
      preConfirm: () => {
        const priceType = document.getElementById('priceType').value;
        const price = document.getElementById('price').value;

        if (!priceType) {
          Swal.showValidationMessage('Price type is required');
          return;
        }

        if (!price || Number(price) <= 0) {
          Swal.showValidationMessage('Please enter a valid price');
          return;
        }

        return {
          priceType,
          price: Number(price),
        };
      },
    });

    if (!formValues) return;

    try {
      const token = localStorage.getItem('token');

      const res = await fetch(`${config.apiBaseUrl}/admin/upsale/property/edit-product`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          propertyId,
          productId: product.id,
          priceType: formValues.priceType,
          price: Number(formValues.price),
          taxPercent: 0,
          extraCharges: 0,
        }),
      });

      const data = await res.json();

      if (data.success) {
        Swal.fire('Success', 'Product updated successfully', 'success');
        fetchMappedHierarchy();
      } else {
        Swal.fire('Error', data.message || 'Update failed', 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Something went wrong', 'error');
    }
  };

  const selectedCategoryData = mappedHierarchy.find((c) => c.id === selectedTableCategory);

  const selectedSubCategoryData = selectedCategoryData?.subCategories?.find(
    (s) => s.id === selectedTableSubCategory
  );

  const finalProducts = selectedCategoryData?.hasSubCategory
    ? selectedSubCategoryData?.products || []
    : selectedCategoryData?.products || [];
  const DEFAULT_IMAGE = 'https://via.placeholder.com/300x200?text=No+Image';

  return (
    <Box sx={{ px: 2, py: 2 }}>
      {showAddMapping ? (
        <AddMappingView
          propertyId={propertyId}
          onClose={() => setShowAddMapping(false)}
          onSuccess={() => {
            setShowAddMapping(false);
            fetchMappedHierarchy();
          }}
        />
      ) : (
        <>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <IconButton onClick={() => window.history.back()}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight="bold" sx={{ flexGrow: 1 }}>
              Upsell Mapping
            </Typography>
            <Button variant="contained" onClick={() => setShowAddMapping(true)}>
              Add Mapping
            </Button>
          </Stack>

          <Box sx={{ display: 'flex', border: '1px solid #e5e7eb' }}>
            {/* -------- CATEGORY -------- */}
            <TableContainer component={Paper} sx={{ flex: 1, borderRight: '1px solid #e5e7eb' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                    <TableCell align="center">
                      <b>Category</b>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    [...Array(6)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Skeleton height={28} />
                        </TableCell>
                        <TableCell>
                          <Skeleton height={28} />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : mappedHierarchy.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        No Category
                      </TableCell>
                    </TableRow>
                  ) : (
                    mappedHierarchy.map((cat) => {
                      const disableDelete =
                        (cat.products?.length ?? 0) > 0 || (cat.subCategories?.length ?? 0) > 0;

                      return (
                        <TableRow
                          key={cat.id}
                          hover
                          selected={selectedTableCategory === cat.id}
                          onClick={() => {
                            setSelectedTableCategory(cat.id);
                            setSelectedTableSubCategory(cat.subCategories?.[0]?.id || null);
                          }}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>{cat.name}</TableCell>

                          <TableCell align="right">
                            <IconButton
                              disabled={disableDelete || deleting[cat.id]}
                              onClick={(e) => {
                                e.stopPropagation();
                                removeCategory(cat.id);
                              }}
                              sx={{ color: '#ef4444' }}
                            >
                              {deleting[cat.id] ? (
                                <CircularProgress size={18} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* -------- SUBCATEGORY -------- */}
            <TableContainer component={Paper} sx={{ flex: 1, borderRight: '1px solid #e5e7eb' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                    <TableCell align="center">
                      <b>Subcategory</b>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    [...Array(6)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Skeleton height={28} />
                        </TableCell>
                        <TableCell>
                          <Skeleton height={28} />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : !selectedTableCategory ? (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        Select Category
                      </TableCell>
                    </TableRow>
                  ) : selectedCategoryData?.hasSubCategory === false ? (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        No Subcategory
                      </TableCell>
                    </TableRow>
                  ) : (
                    selectedCategoryData?.subCategories?.map((sub) => {
                      const disableDelete = (sub.products?.length ?? 0) > 0;

                      return (
                        <TableRow
                          key={sub.id}
                          hover
                          selected={selectedTableSubCategory === sub.id}
                          onClick={() => setSelectedTableSubCategory(sub.id)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>{sub.name}</TableCell>

                          <TableCell align="right">
                            <IconButton
                              disabled={disableDelete || deleting[sub.id]}
                              onClick={(e) => {
                                e.stopPropagation();
                                removeSubCategory(sub.id);
                              }}
                              sx={{ color: '#ef4444' }}
                            >
                              {deleting[sub.id] ? (
                                <CircularProgress size={18} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* -------- PRODUCT -------- */}
            <TableContainer component={Paper} sx={{ flex: 1, borderRight: '1px solid #e5e7eb' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                    <TableCell align="center">
                      <b>Product</b>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  <TableRow>
                    <TableCell colSpan={1} sx={{ p: 2 }}>
                      <Grid container spacing={2} sx={{ width: '100%', m: 0 }}>
                        {loading ? (
                          [...Array(6)].map((_, i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                              <Card sx={{ p: 1 }}>
                                <Skeleton variant="rectangular" height={140} />
                                <CardContent>
                                  <Skeleton height={24} />
                                  <Skeleton width="60%" />
                                </CardContent>
                              </Card>
                            </Grid>
                          ))
                        ) : !selectedTableCategory ? (
                          <Grid item xs={12}>
                            <Typography align="center">Select Category</Typography>
                          </Grid>
                        ) : finalProducts.length === 0 ? (
                          <Grid item xs={12}>
                            <Typography align="center">No Products</Typography>
                          </Grid>
                        ) : (
                          finalProducts.map((prod) => (
                            <Grid item xs={12} sm={6} md={4} key={prod.id}>
                              <Card
                                sx={{
                                  height: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  borderRadius: 2,
                                  boxShadow: 2,
                                  '&:hover': { boxShadow: 4 },
                                }}
                              >
                                {/* Image */}
                                {prod.image ? (
                                  <Box
                                    component="img"
                                    src={prod.image}
                                    alt={prod.name}
                                    onError={(e) => (e.currentTarget.style.display = 'none')}
                                    sx={{
                                      height: 80,
                                      width: '100%',
                                      objectFit: 'cover',
                                    }}
                                  />
                                ) : (
                                  <Box
                                    sx={{
                                      height: 80,
                                      width: '100%',
                                      bgcolor: '#f3f4f6',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      position: 'relative',
                                    }}
                                  >
                                    {/* First Letter */}
                                    <Avatar
                                      sx={{
                                        bgcolor: '#155dedff',
                                        color: '#374151',
                                        width: 48,
                                        height: 48,
                                        fontWeight: 'bold',
                                        fontSize: 20,
                                      }}
                                    >
                                      {prod.name?.charAt(0)?.toUpperCase()}
                                    </Avatar>

                                    {/* Camera Icon */}
                                    <CameraAltIcon
                                      sx={{
                                        position: 'absolute',
                                        bottom: 6,
                                        right: 6,
                                        fontSize: 18,
                                        color: '#6b7280',
                                      }}
                                    />
                                  </Box>
                                )}

                                <CardContent sx={{ flexGrow: 1 }}>
                                  <Typography fontWeight={600}>{prod.name}</Typography>

                                  <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                                    £{prod.price}
                                  </Typography>
                                </CardContent>

                                <CardActions sx={{ justifyContent: 'flex-end', px: 2 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditProduct(prod)}
                                    sx={{ color: '#2563eb' }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    onClick={() => removeProduct(prod.id)}
                                    sx={{ color: '#ef4444' }}
                                    disabled={deleting[prod.id]}
                                  >
                                    {deleting[prod.id] ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <DeleteIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </CardActions>
                              </Card>
                            </Grid>
                          ))
                        )}
                      </Grid>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </>
      )}
    </Box>
  );
}
